import React, { useState } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { X, Book, Users, CreditCard, PauseCircle, ChevronRight, QrCode, PlayCircle, Video, Briefcase, CalendarDays } from 'lucide-react';

interface HelpModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type Language = 'en' | 'hi' | 'te';

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
    const [lang, setLang] = useState<Language>('en');
    const [selectedVideo, setSelectedVideo] = useState<{ id: string; title: string } | null>(null);

    if (!isOpen) return null;

    const content = {
        en: {
            title: 'Gym Management Help Guide',
            sections: [
                {
                    id: 'tutorials',
                    title: 'Video Tutorials',
                    icon: <Video className="w-5 h-5 text-red-500" />,
                    intro: 'Watch these quick demo videos to master Gympe and manage your gym efficiently.',
                    type: 'videos',
                    videos: [
                        {
                            title: '1. Manage Plans',
                            desc: 'Learn how to create, edit, and manage your membership plans with advanced settings.',
                            youtubeId: 'AJ6wF-UvwV8'
                        },
                        {
                            title: '2. Manage Members & Memberships',
                            desc: 'Master member management, from onboarding to tracking active memberships.',
                            youtubeId: 'AXvTTsvnJYo'
                        }
                    ]
                },
                {
                    id: 'members',
                    title: 'Members',
                    icon: <Users className="w-5 h-5 text-blue-500" />,
                    intro: 'A member is a person associated with your gym. Each member has a status that shows their relationship with your business.',
                    subsections: [
                        {
                            title: 'Member statuses 👤',
                            items: [
                                { label: 'Lead', desc: 'A potential customer who has not yet joined.' },
                                { label: 'Trial', desc: 'A member currently using a trial period.' },
                                { label: 'Active', desc: 'A member who has an active or paused membership.' },
                                { label: 'Inactive', desc: 'A member who does not currently have an active membership.' },
                                { label: 'Suspended', desc: 'A member temporarily blocked from using gym services.' },
                                { label: 'Deleted', desc: 'A member who has been removed from the system.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'memberships',
                    title: 'Memberships',
                    icon: <CreditCard className="w-5 h-5 text-emerald-500" />,
                    intro: 'A membership represents a plan purchased by a member. Memberships never overlap.',
                    subsections: [
                        {
                            title: 'Membership statuses 💳',
                            items: [
                                { label: 'Active', desc: 'The membership is currently valid.' },
                                { label: 'Planned', desc: 'A future membership that will start after the current one ends.' },
                                { label: 'Paused (automatic)', desc: 'The membership is temporarily paused.' },
                                { label: 'Expired (automatic)', desc: 'The membership end date has passed.' },
                                { label: 'Cancelled', desc: 'The membership was manually cancelled.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'pauses',
                    title: 'Membership Pauses',
                    icon: <PauseCircle className="w-5 h-5 text-orange-500" />,
                    intro: 'A pause temporarily stops a membership without losing the remaining days.',
                    subsections: [
                        {
                            title: 'How pauses work ⏸️',
                            items: [
                                { label: 'Trigger', desc: 'Pauses can only be applied to an active membership.' },
                                { label: 'Effect', desc: 'While paused: The member cannot use the membership, and the end date is extended automatically.' },
                                { label: 'Resumption', desc: 'When a pause is cancelled, the end date adjusts back accordingly.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'attendance',
                    title: 'Attendance',
                    icon: <QrCode className="w-5 h-5 text-indigo-500" />,
                    intro: 'Track daily entries and manage gym floor access via QR scanning.',
                    subsections: [
                        {
                            title: 'How to scan 🔍',
                            items: [
                                { label: 'Camera Access', desc: 'Go to the Attendance tab and click "Start Scanning" to open your device camera.' },
                                { label: 'Member QR Code', desc: 'Members can find their QR code in their profile section.' },
                                { label: 'Verification', desc: 'The system validates the QR code instantly to ensure a valid membership.' },
                                { label: 'Manual Entry', desc: 'Search for members in the Members tab and check them in manually if needed.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'team',
                    title: 'Team & HRMS',
                    icon: <Briefcase className="w-5 h-5 text-violet-500" />,
                    intro: 'Manage your trainers, admins, and staff. Track their leaves, salaries, attendance, and payslips — all in one place.',
                    subsections: [
                        {
                            title: 'Team Members 👥',
                            items: [
                                { label: 'Roles', desc: 'Each team member has a role: Admin, Trainer, or Staff. The role determines what they can manage.' },
                                { label: 'Branch Access', desc: 'A team member can be assigned to one or more branches. Attendance check-in is restricted to assigned branches. Admins can only manage members in their assigned branches.' },
                                { label: 'Employee ID', desc: 'Auto-generated on creation. Used to identify the employee on payslips.' },
                                { label: 'Login Access', desc: 'Enable "Can Login" to allow the team member to log into the app with their email.' },
                            ]
                        },
                        {
                            title: 'Leaves 🏖️',
                            items: [
                                { label: 'Adding a Leave', desc: 'Go to Team → Leaves tab and click "Add Leave". Select the team member, leave type, and date range.' },
                                { label: 'Half Day', desc: 'Check the "Half Day" option to record only half a working day.' },
                                { label: 'Approval Rules', desc: 'Owners can approve leaves for all team members including admins. Admins can only approve leaves for trainers and staff.' },
                                { label: 'Editing Leaves', desc: 'Only pending leaves can be edited (type, dates, reason). Click "Edit" on a pending leave row.' },
                                { label: 'Deleting Leaves', desc: 'Any leave can be deleted. Data is retained in the system (soft delete) but hidden from the list.' },
                                { label: 'Date Limits', desc: 'Leaves can be added up to 1 month in the past and up to 2 months in the future.' },
                                { label: 'Overlap Check', desc: 'The system prevents two leaves from being created for overlapping dates.' },
                            ]
                        },
                        {
                            title: 'Salaries 💰',
                            items: [
                                { label: 'Salary Record', desc: 'Set a base salary for each team member with an effective date. Multiple salary records track pay history over time.' },
                                { label: 'Working Hours', desc: 'Configure working hours per day and working days per month — used for payslip calculations.' },
                                { label: 'Overtime & Undertime', desc: 'Set per-hour rates for overtime and undertime deductions.' },
                            ]
                        },
                        {
                            title: 'Payslips 📄',
                            items: [
                                { label: 'Generating a Payslip', desc: 'Go to Team → Payslips tab and click "Generate Payslip". Select a team member and the pay period. Attendance data is auto-fetched.' },
                                { label: 'Components', desc: 'Add earnings (basic salary, allowances, bonuses) and deductions (PF, ESI, TDS) as line items.' },
                                { label: 'Draft vs Finalized', desc: 'Payslips start as Draft. Once reviewed, click Finalize to lock the payslip. Finalized payslips can be downloaded as PDF.' },
                                { label: 'Finalization Rules', desc: 'Owners can finalize payslips for all team members including admins. Admins can only finalize payslips for trainers and staff.' },
                                { label: 'Download PDF', desc: 'Finalized payslips can be downloaded as a formatted PDF from the list or the payslip detail view.' },
                                { label: 'Deleting Payslips', desc: 'Any payslip can be deleted. Data is retained in the system (soft delete) but removed from the list.' },
                            ]
                        },
                        {
                            title: 'Team Attendance ⏱️',
                            items: [
                                { label: 'Check-in', desc: 'Team members can check in to branches they are assigned to. Owners and admins can log attendance manually.' },
                                { label: 'Reports', desc: 'View daily and monthly attendance summaries per team member on the Attendance tab.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'class_scheduling',
                    title: 'Bookings',
                    icon: <CalendarDays className="w-5 h-5 text-blue-500" />,
                    intro: 'Let members browse and book classes, spa & recovery, personal training, consults and courts from their app. Everything bookable is a pre-scheduled session you create in advance.',
                    subsections: [
                        {
                            title: 'The five bookable types 🎯',
                            items: [
                                { label: 'Class', desc: 'A group session (Yoga, HIIT…) — many members per slot, default capacity 20.' },
                                { label: 'Spa / Personal / Consult', desc: '1:1 slots — a sports massage, a PT block, a nutrition consult. Capacity defaults to 1; PT slots need a trainer assigned.' },
                                { label: 'Facility', desc: 'A self-service resource — a court, steam room, cricket net, swimming pool… Every facility session needs the resource assigned and no staff member. Members can only book slots that already exist — generate a full day of facility slots with a recurring template.' },
                            ]
                        },
                        {
                            title: 'How members can access bookings 🏋️',
                            items: [
                                { label: 'Via Membership Plan', desc: 'Add access inside a plan (Plans → edit plan → Bookings access tab). It is grouped by type, so you can grant "4 PT sessions/month + unlimited classes". Members on that plan book for free — sessions deduct from their monthly allowance automatically.' },
                                { label: 'Credit Pack', desc: 'Sell a pack of session credits as a Purchase (e.g. "10 Yoga sessions"). Go to Purchases, add a new purchase with a service that has credits attached. Credits are deducted per booking. You can set an expiry date on the pack.' },
                                { label: 'Pay Per Session', desc: 'Members pay each time they book. This requires a Service (with a price and GST) linked to the session. The system auto-generates an invoice on booking.' },
                            ]
                        },
                        {
                            title: 'Booking rules ⏰',
                            items: [
                                { label: 'Booking window', desc: 'Set "Booking opens X hours before" on a bookable to control how early members can book. Before that window, the button shows "Opens at [time]" in the member app.' },
                                { label: 'Cancellation cutoff', desc: 'Set "Cancel deadline X hours before" on a bookable. Members cannot cancel after this point. The cutoff also determines if credits are refunded on cancellation.' },
                                { label: 'Waitlist', desc: 'When a session is full, members can join the waitlist. If someone cancels, the next person on the waitlist is automatically notified.' },
                                { label: 'Auto no-show', desc: 'Bookings still unmarked a couple of hours after a session ends are automatically marked no-show (labelled "auto" in the roster). Staff can correct any auto mark by checking the member in.' },
                            ]
                        },
                        {
                            title: 'Setup checklist ✅',
                            items: [
                                { label: '1. Enable the feature', desc: 'Bookings must be enabled on your gym plan (Settings → Features).' },
                                { label: '2. Create bookables', desc: 'Go to Bookings → New Bookable. Pick the type first — it presets capacity, duration and cancellation window — then name, category and the rest.' },
                                { label: '3. Add resources', desc: 'In Bookings → Resources, add rooms, studios, courts and therapy rooms per branch. Courts are required for court sessions.' },
                                { label: '4. Schedule sessions', desc: 'Use recurring templates (Bookings → Recurring tab) to bulk-generate sessions. Courts and spa need their slots generated ahead — members can only book what exists.' },
                                { label: '5. Configure access', desc: 'Choose how members pay: add to a plan, sell a credit pack, or link a pricing service to the session.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'rules',
                    title: 'Important Rules',
                    icon: <Book className="w-5 h-5 text-purple-500" />,
                    intro: 'Keep these key rules in mind for smooth management.',
                    subsections: [
                        {
                            title: 'Key Constraints ⚡',
                            items: [
                                { label: 'Simultaneity', desc: 'Only one active and one planned membership allowed at a time.' },
                                { label: 'Overlap', desc: 'Memberships cannot overlap dates.' },
                                { label: 'Dependencies', desc: 'Cannot pause if a planned membership exists.' },
                            ]
                        }
                    ]
                }
            ]
        },
        hi: {
            title: 'जिम मैनेजमेंट सहायता गाइड',
            sections: [
                {
                    id: 'tutorials',
                    title: 'वीडियो ट्यूटोरियल',
                    icon: <Video className="w-5 h-5 text-red-500" />,
                    intro: 'जिमपे में महारत हासिल करने के लिए इन त्वरित डेमो वीडियो को देखें।',
                    type: 'videos',
                    videos: [
                        {
                            title: '1. प्लान मैनेज करें (Manage Plans)',
                            desc: 'सदस्यता प्लान बनाना, एडिट करना और मैनेज करना सीखें।',
                            youtubeId: 'AJ6wF-UvwV8'
                        },
                        {
                            title: '2. सदस्य और सदस्यता मैनेज करें',
                            desc: 'सदस्यों को जोड़ने से लेकर उनकी सदस्यता ट्रैक करने तक सब कुछ जानें।',
                            youtubeId: 'AXvTTsvnJYo'
                        }
                    ]
                },
                {
                    id: 'members',
                    title: 'सदस्य (Members)',
                    icon: <Users className="w-5 h-5 text-blue-500" />,
                    intro: 'सदस्य वह व्यक्ति है जो आपके जिम से जुड़ा हुआ है।',
                    subsections: [
                        {
                            title: 'सदस्य की स्थिति 👤',
                            items: [
                                { label: 'Lead (लीड)', desc: 'संभावित ग्राहक जिसने अभी जॉइन नहीं किया है।' },
                                { label: 'Trial (ट्रायल)', desc: 'सदस्य जो ट्रायल पीरियड में है।' },
                                { label: 'Active (सक्रिय)', desc: 'सदस्य जिसके पास सक्रिय या रुका हुआ (paused) सदस्यता है।' },
                                { label: 'Inactive (निष्क्रिय)', desc: 'सदस्य जिसके पास कोई सक्रिय सदस्यता नहीं है।' },
                                { label: 'Suspended (निलंबित)', desc: 'सदस्य को अस्थायी रूप से सेवाओं से रोका गया है।' },
                                { label: 'Deleted (हटाया गया)', desc: 'सदस्य को सिस्टम से हटा दिया गया है।' },
                            ]
                        }
                    ]
                },
                {
                    id: 'memberships',
                    title: 'सदस्यता (Memberships)',
                    icon: <CreditCard className="w-5 h-5 text-emerald-500" />,
                    intro: 'सदस्यता वह प्लान है जो सदस्य खरीदता है।',
                    subsections: [
                        {
                            title: 'सदस्यता की स्थिति 💳',
                            items: [
                                { label: 'Active (सक्रिय)', desc: 'सदस्यता वर्तमान में मान्य है।' },
                                { label: 'Planned (भविष्य की)', desc: 'भविष्य में शुरू होने वाली सदस्यता।' },
                                { label: 'Paused (रुकी हुई)', desc: 'सदस्यता अस्थायी रूप से रोकी गई है। (सिस्टम द्वारा)' },
                                { label: 'Expired (समाप्त)', desc: 'सदस्यता की तारीख खत्म हो चुकी है।' },
                                { label: 'Cancelled (रद्द)', desc: 'सदस्यता को मैन्युअली रद्द किया गया है।' },
                            ]
                        }
                    ]
                },
                {
                    id: 'pauses',
                    title: 'सदस्यता विराम (Pauses)',
                    icon: <PauseCircle className="w-5 h-5 text-orange-500" />,
                    intro: 'Pause सदस्यता को कुछ समय के लिए रोकने की सुविधा देता है।',
                    subsections: [
                        {
                            title: 'यह कैसे काम करता है ⏸️',
                            items: [
                                { label: 'लगाने का नियम', desc: 'Pause केवल सक्रिय सदस्यता पर लगाया जा सकता है।' },
                                { label: 'विराम के दौरान', desc: 'सदस्य जिम का उपयोग नहीं कर सकता।' },
                                { label: 'वापसी', desc: 'Pause हटाने पर अंतिम तारीख वापस समायोजित हो जाती है।' },
                            ]
                        }
                    ]
                },
                {
                    id: 'attendance',
                    title: 'हाजिरी (Attendance)',
                    icon: <QrCode className="w-5 h-5 text-indigo-500" />,
                    intro: 'QR स्कैनिंग के जरिए दैनिक एंट्री मैनेज करें।',
                    subsections: [
                        {
                            title: 'स्कैन कैसे करें 🔍',
                            items: [
                                { label: 'स्कैन प्रक्रिया', desc: 'हाजिरी टैब पर "स्केनिंग शुरू करें" बटन पर क्लिक करें।' },
                                { label: 'सदस्य QR कोड', desc: 'सदस्य अपना QR कोड अपनी प्रोफाइल में देख सकते हैं।' },
                                { label: 'पुष्टि', desc: 'सिस्टम तुरंत चेक करता है कि सदस्य की सदस्यता वैध है या नहीं।' },
                                { label: 'मैनुअल एंट्री', desc: 'मोबाइल भूलने पर आप मैनुअली चेक-इन कर सकते हैं।' },
                            ]
                        }
                    ]
                },
                {
                    id: 'team',
                    title: 'टीम और HRMS',
                    icon: <Briefcase className="w-5 h-5 text-violet-500" />,
                    intro: 'अपने ट्रेनर, एडमिन और स्टाफ को मैनेज करें। छुट्टियाँ, वेतन और पेस्लिप एक जगह ट्रैक करें।',
                    subsections: [
                        {
                            title: 'टीम सदस्य 👥',
                            items: [
                                { label: 'भूमिकाएँ', desc: 'Admin, Trainer, या Staff — भूमिका तय करती है वे क्या मैनेज कर सकते हैं।' },
                                { label: 'ब्रांच एक्सेस', desc: 'टीम सदस्य केवल असाइन की गई ब्रांच में चेक-इन कर सकते हैं।' },
                            ]
                        },
                        {
                            title: 'छुट्टियाँ 🏖️',
                            items: [
                                { label: 'छुट्टी जोड़ें', desc: 'Team → Leaves टैब में जाएं और "Add Leave" पर क्लिक करें।' },
                                { label: 'अनुमोदन नियम', desc: 'मालिक सभी की छुट्टी अप्रूव कर सकता है। एडमिन केवल ट्रेनर/स्टाफ की।' },
                                { label: 'एडिट और डिलीट', desc: 'Pending छुट्टी एडिट की जा सकती है। कोई भी छुट्टी सॉफ्ट डिलीट हो सकती है।' },
                            ]
                        },
                        {
                            title: 'पेस्लिप 📄',
                            items: [
                                { label: 'पेस्लिप बनाएँ', desc: 'Team → Payslips में "Generate Payslip" पर क्लिक करें।' },
                                { label: 'फाइनलाइज़ नियम', desc: 'मालिक सभी की पेस्लिप फाइनल कर सकता है। एडमिन केवल ट्रेनर/स्टाफ की।' },
                                { label: 'PDF और डिलीट', desc: 'Finalized payslip को PDF में डाउनलोड करें। कोई भी payslip सॉफ्ट डिलीट हो सकती है।' },
                            ]
                        }
                    ]
                },
                {
                    id: 'class_scheduling',
                    title: 'बुकिंग्स (Bookings)',
                    icon: <CalendarDays className="w-5 h-5 text-blue-500" />,
                    intro: 'सदस्य अपने ऐप से क्लास, स्पा और रिकवरी, पर्सनल ट्रेनिंग, कंसल्ट और कोर्ट बुक कर सकते हैं। हर बुक होने वाली चीज़ एक पहले से शेड्यूल किया हुआ सेशन है, जिसे आप पहले बनाते हैं।',
                    subsections: [
                        {
                            title: 'पाँच प्रकार की बुकिंग 🎯',
                            items: [
                                { label: 'Class (क्लास)', desc: 'ग्रुप सेशन (योगा, HIIT…) — एक स्लॉट में कई सदस्य, डिफ़ॉल्ट क्षमता 20।' },
                                { label: 'Spa / Personal / Consult', desc: '1:1 स्लॉट — स्पोर्ट्स मसाज, PT ब्लॉक, या न्यूट्रिशन कंसल्ट। क्षमता डिफ़ॉल्ट 1 होती है; PT स्लॉट में ट्रेनर असाइन करना ज़रूरी है।' },
                                { label: 'Court (कोर्ट)', desc: 'बुक करने योग्य कोर्ट या सुविधा (जैसे Badminton Court 1)। हर कोर्ट सेशन में कोर्ट रिसोर्स असाइन करना ज़रूरी है। सदस्य केवल पहले से मौजूद स्लॉट ही बुक कर सकते हैं — रिकरिंग टेम्पलेट से पूरे दिन के स्लॉट एक साथ जेनरेट करें।' },
                            ]
                        },
                        {
                            title: 'सदस्यों को एक्सेस कैसे दें 🏋️',
                            items: [
                                { label: 'मेम्बरशिप प्लान के ज़रिए', desc: 'प्लान में एक्सेस जोड़ें (Plans → प्लान एडिट करें → Bookings access टैब)। यह प्रकार के अनुसार बँटा है, इसलिए "4 PT सेशन/माह + अनलिमिटेड क्लास" जैसा प्लान बना सकते हैं। उस प्लान के सदस्य मुफ़्त में बुक करेंगे।' },
                                { label: 'क्रेडिट पैक', desc: 'एक क्रेडिट पैक बेचें (जैसे "10 Yoga सेशन")। Purchases में जाएं और क्रेडिट के साथ सर्विस जोड़ें। हर बुकिंग पर क्रेडिट कटता है। एक्सपायरी डेट भी सेट कर सकते हैं।' },
                                { label: 'प्रति सेशन भुगतान', desc: 'हर बुकिंग पर अलग से चार्ज होता है। इसके लिए सेशन में एक प्राइस सर्विस लिंक करनी होती है। सिस्टम बुकिंग पर इनवॉइस खुद बनाता है।' },
                            ]
                        },
                        {
                            title: 'बुकिंग के नियम ⏰',
                            items: [
                                { label: 'बुकिंग विंडो', desc: 'बुकेबल पर "बुकिंग X घंटे पहले खुले" सेट करें। उससे पहले सदस्य को "Opens at [time]" दिखेगा।' },
                                { label: 'कैंसिलेशन कट-ऑफ', desc: 'बुकेबल पर "X घंटे पहले कैंसिल करें" सेट करें। इसके बाद कैंसिलेशन नहीं होगी और क्रेडिट वापस नहीं मिलेगा।' },
                                { label: 'वेटलिस्ट', desc: 'सेशन फुल होने पर सदस्य वेटलिस्ट में जुड़ सकते हैं। कोई कैंसिल करे तो अगले सदस्य को अपने आप सूचना जाती है।' },
                                { label: 'ऑटो नो-शो', desc: 'सेशन खत्म होने के कुछ घंटे बाद भी अनमार्क रही बुकिंग अपने आप नो-शो हो जाती है (रोस्टर में "auto" लेबल)। स्टाफ चेक-इन करके इसे सुधार सकता है।' },
                            ]
                        },
                        {
                            title: 'सेटअप चेकलिस्ट ✅',
                            items: [
                                { label: '1. फीचर ऑन करें', desc: 'Settings → Features में Bookings को एनेबल करें।' },
                                { label: '2. बुकेबल बनाएं', desc: 'Bookings → New Bookable में जाएं। सबसे पहले प्रकार चुनें — इससे क्षमता, अवधि और कैंसिलेशन विंडो अपने आप सेट होती हैं — फिर नाम, कैटेगरी आदि भरें।' },
                                { label: '3. रिसोर्स जोड़ें', desc: 'Bookings → Resources में हर शाखा के कमरे, स्टूडियो, कोर्ट और थेरेपी रूम जोड़ें। कोर्ट सेशन के लिए कोर्ट ज़रूरी है।' },
                                { label: '4. सेशन शेड्यूल करें', desc: 'Bookings → Recurring टैब से रिकरिंग टेम्पलेट बनाएं और एक बार में कई सेशन जेनरेट करें। कोर्ट और स्पा के स्लॉट पहले से जेनरेट करने ज़रूरी हैं — सदस्य केवल मौजूद स्लॉट बुक कर सकते हैं।' },
                                { label: '5. एक्सेस कॉन्फिगर करें', desc: 'प्लान में जोड़ें, क्रेडिट पैक बेचें, या सेशन में प्राइस सर्विस लिंक करें।' },
                            ]
                        }
                    ]
                },
                {
                    id: 'rules',
                    title: 'महत्वपूर्ण नियम',
                    icon: <Book className="w-5 h-5 text-purple-500" />,
                    intro: 'बेहतर प्रबंधन के लिए इन नियमों का पालन करें।',
                    subsections: [
                        {
                            title: 'मुख्य सीमाएँ ⚡',
                            items: [
                                { label: 'एकल सक्रियता', desc: 'एक समय में केवल एक सक्रिय और एक भविष्य की सदस्यता।' },
                                { label: 'नो ओवरलैప', desc: 'सदस्यताएँ आपस में ओवरलैप नहीं हो सकतीं।' },
                                { label: 'पाबंदी', desc: 'भविष्य की सदस्यता होने पर Pause नहीं लगा सकते।' },
                            ]
                        }
                    ]
                }
            ]
        },
        te: {
            title: 'జిమ్ మేనేజ్‌మెంట్ సహాయ గైడ్',
            sections: [
                {
                    id: 'tutorials',
                    title: 'వీడియో ట్యుటోరియల్స్',
                    icon: <Video className="w-5 h-5 text-red-500" />,
                    intro: 'జిమ్‌పేని సులువుగా నిర్వహించడానికి ఈ దెమో వీడియోలను చూడండి.',
                    type: 'videos',
                    videos: [
                        {
                            title: '1. ప్లాన్‌లను నిర్వహించండి',
                            desc: 'సభ్యత్వ ప్లాన్‌లను ఎలా సృష్టించాలో మరియు నిర్వహించాలో తెలుసుకోండి.',
                            youtubeId: 'AJ6wF-UvwV8'
                        },
                        {
                            title: '2. సభ్యులు మరియు సభ్యత్వాలు',
                            desc: 'సభ్యులను చేర్చుకోవడం మరియు వారి ప్లాన్‌లను పర్యవేక్షించడం నేర్చుకోండి.',
                            youtubeId: 'AXvTTsvnJYo'
                        }
                    ]
                },
                {
                    id: 'members',
                    title: 'సభ్యులు (Members)',
                    icon: <Users className="w-5 h-5 text-blue-500" />,
                    intro: 'సభ్యుడు అంటే మీ జిమ్తో సంబంధం ఉన్న వ్యక్తి.',
                    subsections: [
                        {
                            title: 'సభ్యుల స్థితులు 👤',
                            items: [
                                { label: 'Lead (లీడ్)', desc: 'ఇంకా జాయిన్ కాని కస్టమర్.' },
                                { label: 'Trial (ట్రయల్)', desc: 'ట్రయల్ పీరియడ్లో ఉన్న సభ్యుడు.' },
                                { label: 'Active (సక్రియ)', desc: 'సక్రియమైన సభ్యత్వం ఉన్న సభ్యుడు.' },
                                { label: 'Inactive (నిష్క్రియ)', desc: 'సక్రియ సభ్యత్వం లేని సభ్యుడు.' },
                                { label: 'Suspended (సస్పెండ్)', desc: 'తాత్కాలికంగా ఆపబడిన సభ్యుడు.' },
                                { label: 'Deleted (తొలగించబడింది)', desc: 'తొలగించబడిన సభ్యుడు.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'memberships',
                    title: 'సభ్యత్వాలు (Memberships)',
                    icon: <CreditCard className="w-5 h-5 text-emerald-500" />,
                    intro: 'సభ్యత్వం అంటే సభ్యుడు కొనుగోలు చేసిన ప్లాన్.',
                    subsections: [
                        {
                            title: 'సభ్యత్వ స్థితులు 💳',
                            items: [
                                { label: 'Active (సక్రియ)', desc: 'చెల్లుబాటు అయ్యే సభ్యత్వం.' },
                                { label: 'Planned (భవిష్యత్)', desc: 'భవిష్యత్తులో ప్రారంభమయ్యే సభ్యత్వం.' },
                                { label: 'Paused (నిలిపివేయబడింది)', desc: 'తాత్కాలికంగా ఆపబడింది.' },
                                { label: 'Expired (ముగిసింది)', desc: 'గడువు ముగిసింది.' },
                                { label: 'Cancelled (రద్దు)', desc: 'రద్దు చేయబడింది.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'pauses',
                    title: 'సభ్యత్వ విరామం (Pauses)',
                    icon: <PauseCircle className="w-5 h-5 text-orange-500" />,
                    intro: 'Pause సభ్యత్వాన్ని కొంతకాలం ఆపడానికి ఉపయోగిస్తారు.',
                    subsections: [
                        {
                            title: 'ఇది ఎలా పనిచేస్తుంది ⏸️',
                            items: [
                                { label: 'నియమం', desc: 'Pause కేవలం సక్రియ సభ్యత్వం పై మాత్రమే చేయవచ్చు.' },
                                { label: 'సమయంలో', desc: 'సభ్యుడు జిమ్ ఉపయోగించలేడు.' },
                                { label: 'తిరిగి రావడం', desc: 'తేదీ ఆటోమేటిక్ గా సరిచేయబడుతుంది.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'attendance',
                    title: 'అటెండెన్స్ (Attendance)',
                    icon: <QrCode className="w-5 h-5 text-indigo-500" />,
                    intro: 'QR స్కానింగ్ ద్వారా నిర్వహణ.',
                    subsections: [
                        {
                            title: 'స్కాన్ చేయడం ఎలా 🔍',
                            items: [
                                { label: 'స్కానింగ్', desc: '"స్కానింగ్ ప్రారంభించండి" బటన్ క్లిక్ చేయండి.' },
                                { label: 'QR కోడ్', desc: 'ప్రొఫైల్ లో QR కోడ్ చూడవచ్చు.' },
                                { label: 'ధృవీకరణ', desc: 'సభ్యత్వ యాక్టివ్ గా ఉందో లేదో తనిఖీ చేస్తుంది.' },
                                { label: 'మాన్యువల్', desc: 'మాన్యువల్ గా చెక్-ఇన్ చేయవచ్చు.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'team',
                    title: 'టీమ్ మరియు HRMS',
                    icon: <Briefcase className="w-5 h-5 text-violet-500" />,
                    intro: 'ట్రైనర్లు, అడ్మిన్లు మరియు స్టాఫ్‌ను నిర్వహించండి. సెలవులు, జీతాలు మరియు పేస్లిప్‌లు ఒకే చోట ట్రాక్ చేయండి.',
                    subsections: [
                        {
                            title: 'టీమ్ సభ్యులు 👥',
                            items: [
                                { label: 'పాత్రలు', desc: 'Admin, Trainer, లేదా Staff — పాత్ర ఏమి నిర్వహించగలరో నిర్ణయిస్తుంది.' },
                                { label: 'బ్రాంచ్ యాక్సెస్', desc: 'టీమ్ సభ్యులు కేవలం తమకు అసైన్ చేయబడిన బ్రాంచ్‌లలో చెక్-ఇన్ చేయగలరు.' },
                            ]
                        },
                        {
                            title: 'సెలవులు 🏖️',
                            items: [
                                { label: 'సెలవు జోడించు', desc: 'Team → Leaves ట్యాబ్‌కు వెళ్లి "Add Leave" క్లిక్ చేయండి.' },
                                { label: 'అనుమతి నియమాలు', desc: 'యజమాని అందరి సెలవులు అప్రూవ్ చేయగలరు. అడ్మిన్ కేవలం ట్రైనర్/స్టాఫ్ సెలవులు మాత్రమే.' },
                                { label: 'ఎడిట్ మరియు డిలీట్', desc: 'పెండింగ్ సెలవులు ఎడిట్ చేయవచ్చు. ఏ సెలవైనా సాఫ్ట్ డిలీట్ చేయవచ్చు.' },
                            ]
                        },
                        {
                            title: 'పేస్లిప్‌లు 📄',
                            items: [
                                { label: 'పేస్లిప్ రూపొందించు', desc: 'Team → Payslips లో "Generate Payslip" క్లిక్ చేయండి.' },
                                { label: 'ఫైనలైజ్ నియమాలు', desc: 'యజమాని అందరి పేస్లిప్‌లు ఫైనలైజ్ చేయగలరు. అడ్మిన్ కేవలం ట్రైనర్/స్టాఫ్ పేస్లిప్‌లు మాత్రమే.' },
                                { label: 'PDF మరియు డిలీట్', desc: 'ఫైనలైజ్ చేసిన పేస్లిప్‌ను PDF గా డౌన్‌లోడ్ చేయవచ్చు. ఏ పేస్లిప్‌నైనా సాఫ్ట్ డిలీట్ చేయవచ్చు.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'class_scheduling',
                    title: 'బుకింగ్స్ (Bookings)',
                    icon: <CalendarDays className="w-5 h-5 text-blue-500" />,
                    intro: 'సభ్యులు తమ యాప్ నుండి క్లాస్‌లు, స్పా & రికవరీ, పర్సనల్ ట్రైనింగ్, కన్సల్ట్‌లు మరియు కోర్టులు బుక్ చేసుకోగలరు. బుక్ చేయదగిన ప్రతిదీ మీరు ముందుగా సృష్టించిన సెషన్.',
                    subsections: [
                        {
                            title: 'ఐదు రకాల బుకింగ్‌లు 🎯',
                            items: [
                                { label: 'Class (క్లాస్)', desc: 'గ్రూప్ సెషన్ (యోగా, HIIT…) — ఒక స్లాట్‌లో చాలా మంది సభ్యులు, డిఫాల్ట్ సామర్థ్యం 20.' },
                                { label: 'Spa / Personal / Consult', desc: '1:1 స్లాట్లు — స్పోర్ట్స్ మసాజ్, PT బ్లాక్, లేదా న్యూట్రిషన్ కన్సల్ట్. సామర్థ్యం డిఫాల్ట్‌గా 1; PT స్లాట్‌కు ట్రైనర్ కేటాయించడం తప్పనిసరి.' },
                                { label: 'Court (కోర్టు)', desc: 'బుక్ చేయదగిన కోర్టు లేదా సదుపాయం (ఉదా. Badminton Court 1). ప్రతి కోర్టు సెషన్‌కు కోర్టు రిసోర్స్ తప్పనిసరి. ఇప్పటికే ఉన్న స్లాట్లను మాత్రమే సభ్యులు బుక్ చేయగలరు — రిక్కరింగ్ టెంప్లేట్‌తో రోజంతటి స్లాట్లు ఒకేసారి జనరేట్ చేయండి.' },
                            ]
                        },
                        {
                            title: 'సభ్యులకు యాక్సెస్ ఎలా ఇవ్వాలి 🏋️',
                            items: [
                                { label: 'మెంబర్‌షిప్ ప్లాన్ ద్వారా', desc: 'ప్లాన్‌లో యాక్సెస్ జోడించండి (Plans → ప్లాన్ ఎడిట్ → Bookings access టాబ్). ఇది రకాల వారీగా విభజించబడింది, కాబట్టి "నెలకు 4 PT సెషన్లు + అపరిమిత క్లాస్‌లు" వంటి ప్లాన్ ఇవ్వవచ్చు. ఆ ప్లాన్ ఉన్న సభ్యులు ఉచితంగా బుక్ చేసుకుంటారు.' },
                                { label: 'క్రెడిట్ పాక్', desc: '"10 Yoga సెషన్లు" వంటి క్రెడిట్ పాక్ అమ్మండి. Purchases లో వెళ్ళి క్రెడిట్‌తో సర్వీస్ జోడించండి. ప్రతి బుకింగ్‌పై క్రెడిట్ తగ్గుతుంది. గడువు తేదీ కూడా సెట్ చేయవచ్చు.' },
                                { label: 'సెషన్ కి చెల్లింపు', desc: 'ప్రతిసారి వేరుగా చార్జ్ అవుతుంది. సెషన్‌కు ధర సర్వీస్ లింక్ అవసరం. సిస్టమ్ స్వయంగా ఇన్వాయిస్ తయారు చేస్తుంది.' },
                            ]
                        },
                        {
                            title: 'బుకింగ్ నియమాలు ⏰',
                            items: [
                                { label: 'బుకింగ్ విండో', desc: 'బుకబుల్‌పై "X గంటల ముందు బుకింగ్ తెరుచుకుంటుంది" సెట్ చేయండి. ఆ సమయానికి ముందు "Opens at [time]" చూపిస్తుంది.' },
                                { label: 'రద్దు గడువు', desc: 'బుకబుల్‌పై రద్దు సమయ పరిమితి సెట్ చేయండి. ఆ తర్వాత రద్దు సాధ్యం కాదు, క్రెడిట్ తిరిగి రాదు.' },
                                { label: 'వెయిట్‌లిస్ట్', desc: 'సెషన్ నిండినపుడు సభ్యులు వెయిట్‌లిస్ట్‌లో చేరవచ్చు. ఎవరైనా రద్దు చేస్తే తదుపరి సభ్యుడికి స్వయంగా తెలియజేస్తుంది.' },
                                { label: 'ఆటో నో-షో', desc: 'సెషన్ ముగిసిన కొన్ని గంటల తర్వాత కూడా మార్క్ కాని బుకింగ్‌లు స్వయంచాలకంగా నో-షో అవుతాయి (రోస్టర్‌లో "auto" లేబుల్). స్టాఫ్ చెక్-ఇన్ చేసి సరిదిద్దవచ్చు.' },
                            ]
                        },
                        {
                            title: 'సెటప్ చెక్‌లిస్ట్ ✅',
                            items: [
                                { label: '1. ఫీచర్ ఆన్ చేయండి', desc: 'Settings → Features లో Bookings ఎనేబుల్ చేయండి.' },
                                { label: '2. బుకబుల్స్ సృష్టించండి', desc: 'Bookings → New Bookable లో వెళ్ళండి. ముందుగా రకం ఎంచుకోండి — దానితో సామర్థ్యం, వ్యవధి మరియు రద్దు గడువు స్వయంగా సెట్ అవుతాయి — తర్వాత పేరు, కేటగిరీ పూరించండి.' },
                                { label: '3. రిసోర్సులు జోడించండి', desc: 'Bookings → Resources లో ప్రతి శాఖకు గదులు, స్టూడియోలు, కోర్టులు మరియు థెరపీ గదులు జోడించండి. కోర్టు సెషన్లకు కోర్టు తప్పనిసరి.' },
                                { label: '4. సెషన్‌లు షెడ్యూల్ చేయండి', desc: 'Bookings → Recurring టాబ్ నుండి రిక్కరింగ్ టెంప్లేట్ ద్వారా ఒకేసారి అనేక సెషన్‌లు జనరేట్ చేయండి. కోర్టు మరియు స్పా స్లాట్లు ముందుగా జనరేట్ చేయాలి — ఉన్న స్లాట్లనే సభ్యులు బుక్ చేయగలరు.' },
                                { label: '5. యాక్సెస్ కాన్ఫిగర్ చేయండి', desc: 'ప్లాన్‌లో జోడించండి, క్రెడిట్ పాక్ అమ్మండి, లేదా సెషన్‌కు ధర సర్వీస్ లింక్ చేయండి.' },
                            ]
                        }
                    ]
                },
                {
                    id: 'rules',
                    title: 'ముఖ్య నియమాలు',
                    icon: <Book className="w-5 h-5 text-purple-500" />,
                    intro: 'నిర్వహణ కోసం ఈ నియమాలను పాటించండి.',
                    subsections: [
                        {
                            title: 'ముఖ్య నిబంధనలు ⚡',
                            items: [
                                { label: 'ఒకే ఒక ప్లాన్', desc: 'ఒకేసారి ఒక సక్రియ మరియు ఒక భవిష్యత్ సభ్యత్వం.' },
                                { label: 'ఓవర్లాపింగ్', desc: 'సభ్యత్వాలు కలవకూడదు.' },
                                { label: 'పరిమితులు', desc: 'Planned సభ్యత్వం ఉంటే pause చేయలేరు.' },
                            ]
                        }
                    ]
                }
            ]
        }
    };

    const activeContent = (content as any)[lang];

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-0 sm:p-4" {...backdropClose(onClose)}>
            <div
                className="bg-white rounded-none sm:rounded-2xl shadow-2xl max-w-4xl w-full h-full sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className="bg-blue-600 p-1.5 sm:p-2 rounded-lg sm:rounded-xl text-white flex-shrink-0">
                            <Book className="w-4 h-4 sm:w-5 h-5" />
                        </div>
                        <h2 className="text-base sm:text-xl font-bold text-gray-900 truncate">{activeContent.title}</h2>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 ml-2">
                        {/* Language Selector */}
                        <div className="flex bg-gray-100 p-1 rounded-xl">
                            {['en', 'hi', 'te'].map((l) => (
                                <button
                                    key={l}
                                    onClick={() => setLang(l as Language)}
                                    className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-bold transition-all ${lang === l ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'}`}
                                >
                                    {l === 'en' ? (
                                        <>
                                            <span className="sm:hidden">EN</span>
                                            <span className="hidden sm:inline">English</span>
                                        </>
                                    ) : l === 'hi' ? (
                                        <>
                                            <span className="sm:hidden">HI</span>
                                            <span className="hidden sm:inline">हिंदी</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="sm:hidden">TE</span>
                                            <span className="hidden sm:inline">తెలుగు</span>
                                        </>
                                    )}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                            aria-label="Close help"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Mobile TOC - Horizontal Scroll */}
                <div className="lg:hidden flex overflow-x-auto py-3 px-4 gap-2 border-b border-gray-50 bg-white sticky top-0 z-10 no-scrollbar">
                    {activeContent.sections.map((section: any) => (
                        <button
                            key={section.id}
                            onClick={() => {
                                document.getElementById(`help-${section.id}`)?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-100 text-xs font-bold text-gray-600 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 transition-all whitespace-nowrap"
                        >
                            <span className="scale-75">{section.icon}</span>
                            {section.title}
                        </button>
                    ))}
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* TOC - Desktop Only */}
                    <div className="hidden lg:block w-64 border-r border-gray-100 p-6 bg-gray-50/30 overflow-y-auto">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">Table of Contents</p>
                        <nav className="space-y-1">
                            {activeContent.sections.map((section: any) => (
                                <a
                                    key={section.id}
                                    href={`#help-${section.id}`}
                                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-all group"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        document.getElementById(`help-${section.id}`)?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                >
                                    <span className="opacity-60 group-hover:opacity-100 transition-opacity">
                                        {section.icon}
                                    </span>
                                    {section.title}
                                    <ChevronRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                                </a>
                            ))}
                        </nav>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 overflow-y-auto p-4 sm:p-10 custom-scrollbar scroll-smooth">
                        <div className="max-w-2xl mx-auto space-y-8 sm:space-y-12">
                            {activeContent.sections.map((section: any) => (
                                <section key={section.id} id={`help-${section.id}`} className="scroll-mt-4 sm:scroll-mt-10">
                                    <div className="flex items-center gap-3 mb-3 sm:mb-4">
                                        <div className="p-2 sm:p-2.5 rounded-xl sm:rounded-2xl bg-gray-50 border border-gray-100">
                                            {section.icon}
                                        </div>
                                        <h3 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">{section.title}</h3>
                                    </div>

                                    <p className="text-gray-600 leading-relaxed mb-6 sm:mb-8 text-base sm:text-lg font-medium">
                                        {section.intro}
                                    </p>

                                    {section.type === 'videos' ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                            {section.videos.map((video: any, i: number) => (
                                                <div 
                                                    key={i}
                                                    onClick={() => setSelectedVideo({ id: video.youtubeId, title: video.title })}
                                                    className="group cursor-pointer bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:border-blue-200 transition-all duration-300"
                                                >
                                                    <div className="relative aspect-video bg-gray-100">
                                                        <img 
                                                            src={`https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`}
                                                            alt={video.title}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                        />
                                                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                                                            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                                                                <PlayCircle className="w-6 h-6" />
                                                            </div>
                                                        </div>
                                                        <div className="absolute top-2 left-2 flex gap-1">
                                                            <div className="bg-blue-600 text-white text-[10px] font-black px-2 py-1 rounded shadow-lg flex items-center gap-1">
                                                                <span>STEP</span>
                                                                <span className="bg-white text-blue-600 rounded-full w-4 h-4 flex items-center justify-center">{i + 1}</span>
                                                            </div>
                                                        </div>
                                                        <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-md text-white text-[10px] font-bold px-2 py-1 rounded">
                                                            DEMO
                                                        </div>
                                                    </div>
                                                    <div className="p-4">
                                                        <h4 className="font-bold text-gray-900 mb-1 line-clamp-1">{video.title}</h4>
                                                        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                                            {video.desc}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="space-y-6 sm:space-y-8">
                                            {section.subsections.map((sub: any, idx: number) => (
                                                <div key={idx} className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-sm">
                                                    <h4 className="text-[10px] sm:text-sm font-black text-gray-400 uppercase tracking-widest mb-4 sm:mb-6 flex items-center gap-2">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                                        {sub.title}
                                                    </h4>
                                                    <div className="space-y-4 sm:space-y-6">
                                                        {sub.items.map((item: any, j: number) => (
                                                            <div key={j} className="group">
                                                                <p className="text-xs sm:text-sm font-bold text-gray-900 mb-1 flex items-center gap-2 transition-transform group-hover:translate-x-1">
                                                                    {item.label}
                                                                </p>
                                                                <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                                                                    {item.desc}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Subtle Divider */}
                                    <div className="mt-8 sm:mt-12 pt-8 sm:pt-12 border-t border-gray-50"></div>
                                </section>
                            ))}

                            {/* Footer */}
                            <footer className="text-center py-6 sm:py-10 opacity-40">
                                <div className="w-10 h-1 bg-gray-200 mx-auto rounded-full mb-4"></div>
                                <p className="text-[8px] sm:text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 text-center">
                                    End of Help Guide • Gympe SaaS
                                </p>
                            </footer>
                        </div>
                    </div>
                </div>
            </div>

            {/* Video Player Modal */}
            {selectedVideo && (
                <div className="fixed inset-0 bg-black/95 z-[2000] flex flex-col animate-in fade-in duration-300">
                    <div className="flex items-center justify-between p-4 sm:p-6">
                        <h3 className="text-white font-bold text-lg sm:text-xl truncate mr-4">{selectedVideo.title}</h3>
                        <button 
                            onClick={() => setSelectedVideo(null)}
                            className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="flex-1 flex items-center justify-center p-0 sm:p-10">
                        <div className="w-full max-w-6xl aspect-video bg-black shadow-2xl relative">
                            <iframe
                                src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1`}
                                title={selectedVideo.title}
                                className="absolute inset-0 w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HelpModal;
